/**
 * Shared thresholds and helpers for reading a running test's results.
 *
 * The thin-data first view (awaiting / check setup / cannot finish / too
 * early) was retired with the 2026-10 results redesign; the thresholds live on
 * in `testHealth.ts`, which turns them into the Test health card's status.
 */

import type { ClickEventLike } from "@/lib/experiments/outcomeLadder"
import { isNavigationClick } from "@/lib/analytics/clickRegion"
import { formatChance } from "@/lib/experiments/bayes"

/** Each version needs this many visitors before zero conversions reads as a setup problem. */
export const CHECK_SETUP_MIN_VISITORS = 100
/** A test that would need longer than this cannot finish at its traffic. */
export const CANNOT_FINISH_DAYS = 180

/** The earliest day a result can be read: the minimum run or the sample estimate, whichever is later. */
export function earliestCallDay(minDays: number, estimatedTotalDays: number | null): number {
  return Math.max(minDays, estimatedTotalDays ?? 0)
}

/**
 * Share of one version's clicks that landed on navigation (nav-*, mobilenav-*).
 * Built from the top click targets only, so it is a floor, not an exact share.
 * Null when the version has no clicks.
 */
export function navClickShare(events: ClickEventLike[], letter: string, totalClicks: number): number | null {
  if (!(totalClicks > 0)) return null
  const nav = events.reduce((sum, e) => (isNavigationClick(e.target, e.secondary) ? sum + (e.clicks[letter] ?? 0) : sum), 0)
  return Math.min(1, nav / totalClicks)
}

/** "about a third", "about half", "18%": how people say a share. */
export function shareInWords(share: number): string {
  if (share >= 0.45 && share <= 0.55) return "about half"
  if (share >= 0.3 && share < 0.37) return "about a third"
  if (share >= 0.23 && share < 0.27) return "about a quarter"
  return `${Math.round(share * 100)}%`
}

export type CallGate = {
  key: "sample" | "duration" | "chance"
  label: string
  /** 0 to 1 toward the gate. */
  progress: number
  now: string
  needed: string
  met: boolean
  /** Day of the test the gate is met at today's traffic; null when it can't be dated. */
  metOnDay: number | null
}

/**
 * The three gates a call waits on (sample size from the power calc for this
 * goal's baseline, minimum run, chance to beat Control), so one call date can
 * be explained. The latest dated gate sets the call date; the chance can't be
 * dated, it only ever reads met or not yet. Same rule for every goal.
 */
export function callGates(input: {
  totalSessions: number
  requiredSamples: number
  daysRunning: number
  minDays: number
  /** Bayesian chance the leading version beats Control, 0 to 1. */
  chance: number
  /** 0 to 1 */
  targetChance: number
  /** Days to the required sample at today's traffic, null when unknown. */
  estimatedTotalDays: number | null
  formatNumber: (n: number) => string
}): CallGate[] {
  const { totalSessions, requiredSamples, daysRunning, minDays, chance, targetChance, estimatedTotalDays } = input
  const fmt = input.formatNumber
  const clamp = (x: number) => Math.min(1, Math.max(0, x))
  const sampleMet = requiredSamples > 0 && totalSessions >= requiredSamples
  return [
    {
      key: "sample",
      label: "Sample size",
      progress: requiredSamples > 0 ? clamp(totalSessions / requiredSamples) : 0,
      now: fmt(totalSessions),
      needed: requiredSamples > 0 ? fmt(requiredSamples) : "unknown",
      met: sampleMet,
      metOnDay: sampleMet ? daysRunning : estimatedTotalDays,
    },
    {
      key: "duration",
      label: "Minimum run",
      progress: clamp(daysRunning / Math.max(1, minDays)),
      now: `${daysRunning} ${daysRunning === 1 ? "day" : "days"}`,
      needed: `${minDays} days`,
      met: daysRunning >= minDays,
      metOnDay: minDays,
    },
    {
      key: "chance",
      label: `Confidence ≥ ${Math.round(targetChance * 100)}%`,
      progress: clamp(chance / Math.max(0.01, targetChance)),
      now: formatChance(chance),
      needed: `${Math.round(targetChance * 100)}%`,
      met: chance >= targetChance,
      metOnDay: null,
    },
  ]
}

/**
 * Whether a goal's result can be acted on (orange answer, Call test, Ship
 * winner): chance at the bar, sample size and minimum run met, settled
 * (Stripe goals) and test health not Check. Descriptive goals never are.
 */
export function callDecision(input: {
  gates: CallGate[]
  descriptive: boolean
  settling: boolean
  untrusted: boolean
}): { callable: boolean; gatesMet: boolean; blocked: "health" | "gates" | null } {
  const gatesMet = input.gates.filter((g) => g.key !== "chance").every((g) => g.met)
  const chanceMet = input.gates.find((g) => g.key === "chance")?.met ?? false
  const callable = !input.descriptive && !input.settling && !input.untrusted && gatesMet && chanceMet
  return { callable, gatesMet, blocked: input.untrusted ? "health" : gatesMet ? null : "gates" }
}
